// The outbox engine. One cycle in flight at a time; fired on start, on
// regaining connectivity, on every enqueue and every sixty seconds while
// online. A cycle takes up to fifty queued items in enqueue order and sends
// them in one batch; the answer is read item by item.
//
// * network error or 5xx: every item goes back to the queue and the cycle
//   is retried with exponential backoff (see backoff.ts), without limit;
// * 429: the same, waiting what Retry-After says;
// * 401 or 403: the items go back to the queue untouched; the session layer
//   has already changed the state, and the next login triggers a cycle;
// * a rejected item is marked with its code, shown to the operator, and
//   never retried.
//
// INC-06b-2a: a cycle sends only what is due. An item that waits for its next
// attempt is not sent before it, whatever fires the cycle: the periodic timer,
// the network coming back and a new enqueue all find it waiting, and the retry
// that was scheduled still fires. The server's Retry-After is honoured whatever
// the status carries it, the 503 of a transient database error included (D-164),
// and without a session nothing is sent at all. A rejection of the whole batch
// evaluates no item: the items go back to the queue instead of ending there.
//
// INC-06b-2b (D-187): a cycle sends only the signed-in operator's own items.
// Another operator's work on the same device is left exactly as it is — never
// sent under this session's credential, never touched — and travels when that
// person signs in. An item with no author, which the upgrade leaves on a device
// that held no profile, belongs to nobody and is never sent.
//
// INC-05a (D-87, D-89, D-90): the item is the visit document at one
// revision. `created`, `updated` and `unchanged` mark the sent revision as
// synchronised on the draft and take the server's identifier, version and
// statuses; `close_incomplete` returns the draft to started with what the
// server found missing; `visit_closed` freezes the draft. An item marked
// dirty while in flight yields one new item, from the draft as it is once
// the answer was applied, when the cycle ends.
//
// Every attempt of every item leaves a sync_log entry.
import { ApiError, NetworkError, isApiError } from '../api/errors';
import type { ApiClient } from '../api/client';
import type { VisitSyncResponse, VisitSyncResult } from '../api/types';
import {
  applyServerAnswer,
  CONVERSION_CODES,
  neverSynchronised,
  type ServerAnswer,
} from '../app/drafts';
import type {
  FieldcoreDb,
  MissingItem,
  OutboxRecord,
  SyncOutcome,
  VisitDraftRecord,
} from '../db/database';
import { backoffDelayMs } from './backoff';
import { enqueueRevision, requeueDirty } from './documents';

export const BATCH_SIZE = 50;
export const PERIODIC_INTERVAL_MS = 60_000;
export const SYNC_PATH = '/api/v1/sync/visits';
// A change to a draft wakes the engine after this quiet period, so a
// stretch of typing produces one request rather than one per keystroke.
export const ENQUEUE_DEBOUNCE_MS = 1_500;

/** Rejections that answer for one revision and leave the item settled:
 *  the draft moves on and the next change sends the next revision. */
const REVISION_REJECTIONS = new Set(['close_incomplete', 'visit_closed']);

/** INC-06b-2b (D-160): the slot a planned visit names cannot back it any more.
 *  The draft became unplanned when the answer was applied, so the item is
 *  settled and the visit's next revision — the converted one — is queued at
 *  once. The operator sees the conversion and loses nothing it loaded. */
const CONVERSION_REJECTIONS = new Set(Object.keys(CONVERSION_CODES));

export type CycleOutcome =
  | 'idle'
  | 'offline'
  /** No session: nothing is sent, and nothing of the queue is touched. */
  | 'no_session'
  | 'waiting'
  | 'ok'
  | 'network_error'
  | 'server_error'
  | 'rate_limited'
  | 'unauthorized'
  | 'forbidden'
  | 'rejected_batch';

export interface CycleResult {
  outcome: CycleOutcome;
  sent: number;
  created: number;
  unchanged: number;
  rejected: number;
  nextDelayMs: number | null;
}

export interface EngineState {
  running: boolean;
  lastResult: CycleResult | null;
  lastCycleAt: number | null;
  nextAttemptAt: number | null;
  consecutiveFailures: number;
  lastTrigger: TriggerReason | null;
}

export interface EngineDeps {
  db: FieldcoreDb;
  api: ApiClient;
  isOnline?: () => boolean;
  /** Whether a session can send: false while signed out, revoked or suspended. */
  hasSession?: () => boolean;
  /** The signed-in operator, whose items are the only ones a cycle sends (D-187). */
  currentAuthor?: () => string | null;
  now?: () => number;
  random?: () => number;
  /** Quiet period before an enqueue trigger runs a cycle; 0 runs at once. */
  enqueueDebounceMs?: number;
}

export type TriggerReason = 'app_open' | 'online' | 'enqueue' | 'periodic' | 'retry' | 'manual';

type Listener = (state: EngineState) => void;

/** Outcomes that send nothing and end the run: there is no more to do now. */
const SETTLED_OUTCOMES = new Set<CycleOutcome>(['idle', 'offline', 'no_session', 'waiting']);

const IDLE: CycleResult = {
  outcome: 'idle',
  sent: 0,
  created: 0,
  unchanged: 0,
  rejected: 0,
  nextDelayMs: null,
};

/** The answer as the draft reads it: the members it needs, and the details it
 *  knows how to read — what a refused close found missing, and the companion
 *  a companion rejection names. */
function answerOf(answer: VisitSyncResult): ServerAnswer {
  const details = answer.details as
    { missing?: unknown; companion_id?: unknown } | null | undefined;
  return {
    result: answer.result,
    code: answer.code,
    visit_id: answer.visit_id,
    version: answer.version,
    client_revision: answer.client_revision,
    status: answer.status,
    sync_status: answer.sync_status,
    missing: Array.isArray(details?.missing) ? (details.missing as MissingItem[]) : null,
    companion_id: typeof details?.companion_id === 'string' ? details.companion_id : null,
  };
}

/** The code a refusal is kept under: the server's, and for a document its
 *  frozen form refuses, the reason the answer gives when the device has words
 *  for it (INC-14a-3f, point 4): a number outside the physical range is said
 *  as that, and not as a document that does not match its form. */
const SAID_REASONS = new Set(['value_out_of_physical_range']);

function refusalCode(answer: VisitSyncResult | undefined): string {
  if (answer === undefined) return 'missing_result';
  const reason = (answer.details as { reason?: unknown } | null | undefined)?.reason;
  if (
    answer.code === 'document_invalid' &&
    typeof reason === 'string' &&
    SAID_REASONS.has(reason)
  ) {
    return reason;
  }
  return answer.code ?? 'missing_result';
}

function defaultOnline(): boolean {
  // Node has a navigator without onLine: undefined means nothing is known, so online.
  if (typeof navigator === 'undefined') return true;
  // Typed as a boolean, absent at runtime in Node: unknown means online.
  const online: unknown = Reflect.get(navigator, 'onLine');
  return online !== false;
}

export class SyncEngine {
  private readonly db: FieldcoreDb;
  private readonly api: ApiClient;
  private readonly isOnline: () => boolean;
  private readonly hasSession: () => boolean;
  private readonly currentAuthor: () => string | null;
  private readonly now: () => number;
  private readonly random: () => number;
  private readonly enqueueDebounceMs: number;
  private enqueueTimer: ReturnType<typeof setTimeout> | null = null;
  private inflight: Promise<CycleResult> | null = null;
  private rerun = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private periodicTimer: ReturnType<typeof setInterval> | null = null;
  private readonly listeners = new Set<Listener>();
  private state: EngineState = {
    running: false,
    lastResult: null,
    lastCycleAt: null,
    nextAttemptAt: null,
    consecutiveFailures: 0,
    lastTrigger: null,
  };
  private readonly onOnline = (): void => {
    this.trigger('online');
  };

  constructor(deps: EngineDeps) {
    this.db = deps.db;
    this.api = deps.api;
    this.isOnline = deps.isOnline ?? defaultOnline;
    this.hasSession = deps.hasSession ?? ((): boolean => true);
    this.currentAuthor = deps.currentAuthor ?? ((): string | null => null);
    this.now = deps.now ?? (() => Date.now());
    this.random = deps.random ?? Math.random;
    this.enqueueDebounceMs = deps.enqueueDebounceMs ?? ENQUEUE_DEBOUNCE_MS;
  }

  /** Attach the triggers: connectivity, the periodic timer, and one cycle now. */
  start(): void {
    if (typeof window !== 'undefined') window.addEventListener('online', this.onOnline);
    this.periodicTimer ??= setInterval(() => {
      if (this.isOnline()) this.trigger('periodic');
    }, PERIODIC_INTERVAL_MS);
    void this.recoverStale().then(() => {
      this.trigger('app_open');
    });
  }

  /** An item left `sending` by a page that went away mid-request (a reload,
   *  a crash) belongs to no cycle any more: back to the queue, so the next
   *  cycle sends it again. The server answers `unchanged` if it had arrived. */
  async recoverStale(): Promise<number> {
    const at = this.now();
    const author = this.currentAuthor();
    return (
      this.db.outbox_data
        .where('status')
        .equals('sending')
        // D-187: only this session's own items; another operator's are left as
        // they are, and that operator's own next start recovers them.
        .filter((item) => author === null || item.author_id === author)
        .modify({ status: 'queued', updated_at: at })
    );
  }

  stop(): void {
    if (typeof window !== 'undefined') window.removeEventListener('online', this.onOnline);
    if (this.periodicTimer !== null) clearInterval(this.periodicTimer);
    this.periodicTimer = null;
    this.clearRetry();
    if (this.enqueueTimer !== null) clearTimeout(this.enqueueTimer);
    this.enqueueTimer = null;
  }

  getState(): EngineState {
    return this.state;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Fire and forget: start a cycle, or ask the running one to run once more.
   *  An enqueue waits for the quiet period first, so consecutive changes
   *  to a draft travel as one revision. */
  trigger(reason: TriggerReason): void {
    this.setState({ lastTrigger: reason });
    if (reason === 'enqueue' && this.enqueueDebounceMs > 0) {
      if (this.enqueueTimer !== null) clearTimeout(this.enqueueTimer);
      this.enqueueTimer = setTimeout(() => {
        this.enqueueTimer = null;
        void this.syncNow();
      }, this.enqueueDebounceMs);
      return;
    }
    void this.syncNow();
  }

  /** Run a cycle, or join the one in flight. Never rejects: failures are outcomes. */
  syncNow(): Promise<CycleResult> {
    if (this.inflight !== null) {
      this.rerun = true;
      return this.inflight;
    }
    // The scheduled retry is left alone: a cycle that runs now sends what is
    // due now, and the items that still wait are the retry's to send. Cancelling
    // it here would be the trigger bringing their attempt forward.
    this.inflight = this.runUntilSettled().finally(() => {
      this.inflight = null;
    });
    return this.inflight;
  }

  /** Whether a trigger arrived while a cycle ran; reading it clears it. */
  private takeRerun(): boolean {
    const again = this.rerun;
    this.rerun = false;
    return again;
  }

  /** Cycles until the queue is drained or a cycle fails; the result adds up the batches sent. */
  private async runUntilSettled(): Promise<CycleResult> {
    this.setState({ running: true });
    let aggregate: CycleResult | null = null;
    try {
      for (;;) {
        const result = await this.cycle();
        if (SETTLED_OUTCOMES.has(result.outcome)) {
          aggregate ??= result;
          break;
        }
        aggregate =
          aggregate === null
            ? result
            : {
                ...result,
                sent: aggregate.sent + result.sent,
                created: aggregate.created + result.created,
                unchanged: aggregate.unchanged + result.unchanged,
                rejected: aggregate.rejected + result.rejected,
              };
        // Drafts that changed while their item was in flight get their next
        // revision now, from the draft as the answer left it.
        const requeued = await requeueDirty(this.db, this.now(), this.currentAuthor());
        if (result.outcome !== 'ok' || !(result.sent > 0 || requeued > 0 || this.takeRerun())) {
          break;
        }
      }
    } finally {
      this.setState({ running: false, lastResult: aggregate, lastCycleAt: this.now() });
    }
    return aggregate;
  }

  private async cycle(): Promise<CycleResult> {
    // Without a session there is nobody to send as: the queue is left exactly
    // as it is, and the cycle that follows the next sign in sends it.
    if (!this.hasSession()) return { ...IDLE, outcome: 'no_session' };
    if (!this.isOnline()) return { ...IDLE, outcome: 'offline' };
    // Nobody to send as: a session without a profile sends none of the queue,
    // its own included, and leaves every item where it is.
    const author = this.currentAuthor();
    if (author === null) return { ...IDLE, outcome: 'no_session' };
    const startedAt = this.now();
    const all = await this.db.outbox_data.where('status').equals('queued').sortBy('enqueued_at');
    // D-187: only this operator's items. Another operator's are not read past
    // their author and never reach a request.
    const queued = all.filter((item) => item.author_id === author);
    // An item that waits for its next attempt is not sent before it, whatever
    // fired this cycle.
    const due = queued.filter(
      (item) => item.next_attempt_at === null || item.next_attempt_at <= startedAt,
    );
    const batch = due.slice(0, BATCH_SIZE);
    if (batch.length === 0) {
      this.scheduleNextDue(queued, startedAt);
      return queued.length === 0 ? IDLE : { ...IDLE, outcome: 'waiting' };
    }
    await this.db.outbox_data.bulkPut(
      batch.map((item) => ({ ...item, status: 'sending' as const, updated_at: startedAt })),
    );
    try {
      const response = await this.api.post<VisitSyncResponse>(SYNC_PATH, {
        visits: batch.map((item) => item.payload),
      });
      return await this.applyResponse(batch, response);
    } catch (error) {
      return this.applyFailure(batch, error);
    }
  }

  private async applyResponse(
    batch: OutboxRecord[],
    response: VisitSyncResponse,
  ): Promise<CycleResult> {
    const at = this.now();
    const byId = new Map(response.results.map((result) => [result.client_generated_id, result]));
    const result: CycleResult = { ...IDLE, outcome: 'ok', sent: batch.length };
    await this.db.transaction(
      'rw',
      [this.db.outbox_data, this.db.visit_drafts, this.db.sync_log],
      async () => {
        for (const sent of batch) {
          // The stored item may have been marked dirty while in flight.
          const item = (await this.db.outbox_data.get(sent.id)) ?? sent;
          const attempt = item.attempts + 1;
          const answer = byId.get(item.client_generated_id);
          const settled = answer !== undefined && answer.result !== 'rejected';
          const revisionRejection =
            answer?.result === 'rejected' && REVISION_REJECTIONS.has(answer.code ?? '');
          // A conversion settles the item and queues the converted document.
          // The draft is converted here and the next revision is queued once
          // this item is settled below: queueing it now would only mark this
          // very item dirty, which the settling put would then overwrite.
          const converted =
            answer?.result === 'rejected' && CONVERSION_REJECTIONS.has(answer.code ?? '')
              ? await this.convertDraft(item, answer, at)
              : null;
          if (converted === null && answer !== undefined) {
            await this.applyToDraft(item, answer, at);
          }
          // The item ends here: the server refused it and nothing the device
          // did settles it. An answer that never came is the same case.
          const unsettled =
            answer === undefined || (!settled && !revisionRejection && converted === null);
          if (unsettled) {
            const code = refusalCode(answer);
            result.rejected += 1;
            await this.db.outbox_data.put({
              ...item,
              status: 'rejected',
              attempts: attempt,
              next_attempt_at: null,
              last_code: code,
              last_http_status: 200,
              updated_at: at,
            });
            await this.db.sync_log.add({
              outbox_id: item.id,
              attempt,
              at,
              outcome: 'rejected',
              http_status: 200,
              code,
              next_delay_ms: null,
            });
            continue;
          }
          if (answer.result === 'created' || answer.result === 'updated') result.created += 1;
          else if (answer.result === 'unchanged') result.unchanged += 1;
          else result.rejected += 1;
          // A revision rejection is settled too: the answer is on the draft
          // and the item is history, named by its code.
          await this.db.outbox_data.put({
            ...item,
            status: 'synced',
            attempts: attempt,
            next_attempt_at: null,
            last_code: answer.result === 'rejected' ? answer.code : null,
            last_http_status: 200,
            updated_at: at,
          });
          await this.db.sync_log.add({
            outbox_id: item.id,
            attempt,
            at,
            outcome:
              answer.result === 'rejected'
                ? 'rejected'
                : answer.result === 'unchanged'
                  ? 'unchanged'
                  : 'success',
            http_status: 200,
            code: answer.result === 'rejected' ? answer.code : null,
            next_delay_ms: null,
          });
          // D-160: the converted document goes out now, with everything the
          // operator had already loaded, and without it doing anything.
          if (converted !== null) await enqueueRevision(this.db, converted, at);
        }
      },
    );
    this.setState({ consecutiveFailures: 0, nextAttemptAt: null });
    return result;
  }

  /** The server's answer on the draft: identifiers, version, statuses, the
   *  missing list of a refused close, the freeze of a closed visit, and,
   *  since INC-06b-2b, the conversion of a planned visit whose slot is gone
   *  (D-160) and the companion the organization refused (D-110). */
  private async applyToDraft(
    item: OutboxRecord,
    answer: VisitSyncResult,
    at: number,
  ): Promise<void> {
    const draft = await this.db.visit_drafts.get(item.client_generated_id);
    if (draft === undefined) return;
    await this.db.visit_drafts.put(
      applyServerAnswer(draft, item.client_revision, answerOf(answer), at),
    );
  }

  /** D-160: the slot cannot back the visit. The draft becomes unplanned and
   *  its next revision is queued at once, so the work already loaded travels
   *  without the operator doing anything. False when there was nothing to
   *  convert: a visit the server already knows keeps its envelope (D-113),
   *  and that rejection is shown like any other. */
  private async convertDraft(
    item: OutboxRecord,
    answer: VisitSyncResult,
    at: number,
  ): Promise<VisitDraftRecord | null> {
    const draft = await this.db.visit_drafts.get(item.client_generated_id);
    if (draft === undefined) return null;
    if (draft.visit_kind !== 'planned' || !neverSynchronised(draft)) return null;
    const converted = applyServerAnswer(draft, item.client_revision, answerOf(answer), at);
    if (converted.converted_reason === null) return null;
    await this.db.visit_drafts.put(converted);
    return converted;
  }

  private async applyFailure(batch: OutboxRecord[], error: unknown): Promise<CycleResult> {
    const at = this.now();
    let outcome: SyncOutcome;
    let cycleOutcome: CycleOutcome;
    let httpStatus: number | null = null;
    let code: string | null = null;
    let delay: number | null = null;
    let retry = false;
    if (error instanceof NetworkError) {
      outcome = 'network_error';
      cycleOutcome = 'network_error';
      retry = true;
    } else if (isApiError(error)) {
      httpStatus = error.status;
      code = error.code;
      if (error.status === 429) {
        outcome = 'rate_limited';
        cycleOutcome = 'rate_limited';
        retry = true;
        delay = error.retryAfterMs;
      } else if (error.status >= 500) {
        outcome = 'server_error';
        cycleOutcome = 'server_error';
        retry = true;
        // The server says when to come back on a 503 as on a 429: a transient
        // database error stopped the batch at one item (D-164) and the wait is
        // short. Without the header the backoff decides.
        delay = error.retryAfterMs;
      } else if (error.status === 401) {
        outcome = 'unauthorized';
        cycleOutcome = 'unauthorized';
      } else if (error.status === 403) {
        outcome = 'forbidden';
        cycleOutcome = 'forbidden';
      } else {
        // A 4xx over the whole batch (a body the API refuses): not retryable.
        return this.rejectBatch(batch, error, at);
      }
    } else {
      // Anything else is treated like a lost connection: retried, logged.
      outcome = 'network_error';
      cycleOutcome = 'network_error';
      retry = true;
    }
    const failures = retry ? this.state.consecutiveFailures + 1 : this.state.consecutiveFailures;
    if (retry) delay ??= backoffDelayMs(failures, this.random);
    const nextAttemptAt = delay === null ? null : at + delay;
    await this.db.transaction('rw', [this.db.outbox_data, this.db.sync_log], async () => {
      for (const sent of batch) {
        const item = (await this.db.outbox_data.get(sent.id)) ?? sent;
        const attempt = item.attempts + 1;
        await this.db.outbox_data.put({
          ...item,
          status: 'queued',
          attempts: attempt,
          next_attempt_at: nextAttemptAt,
          last_code: code,
          last_http_status: httpStatus,
          updated_at: at,
        });
        await this.db.sync_log.add({
          outbox_id: item.id,
          attempt,
          at,
          outcome,
          http_status: httpStatus,
          code,
          next_delay_ms: delay,
        });
      }
    });
    this.setState({ consecutiveFailures: failures, nextAttemptAt });
    if (retry && delay !== null) this.scheduleRetry(delay);
    return { ...IDLE, outcome: cycleOutcome, sent: batch.length, nextDelayMs: delay };
  }

  /** A 4xx over the whole batch: the server evaluated no item of it.
   *
   *  Since INC-06b-2a the server answers every item it can read on its own, so
   *  this is a body it could not read as a batch at all. Nothing was evaluated,
   *  so nothing ends here: the items go back to the queue with the wait of a
   *  failed cycle, and the attempt is in the log under the batch's code. */
  private async rejectBatch(
    batch: OutboxRecord[],
    error: ApiError,
    at: number,
  ): Promise<CycleResult> {
    const failures = this.state.consecutiveFailures + 1;
    const delay = error.retryAfterMs ?? backoffDelayMs(failures, this.random);
    const nextAttemptAt = at + delay;
    await this.db.transaction('rw', [this.db.outbox_data, this.db.sync_log], async () => {
      for (const sent of batch) {
        const item = (await this.db.outbox_data.get(sent.id)) ?? sent;
        const attempt = item.attempts + 1;
        await this.db.outbox_data.put({
          ...item,
          status: 'queued',
          attempts: attempt,
          next_attempt_at: nextAttemptAt,
          last_code: error.code,
          last_http_status: error.status,
          updated_at: at,
        });
        await this.db.sync_log.add({
          outbox_id: item.id,
          attempt,
          at,
          outcome: 'rejected',
          http_status: error.status,
          code: error.code,
          next_delay_ms: delay,
        });
      }
    });
    this.setState({ consecutiveFailures: failures, nextAttemptAt });
    this.scheduleRetry(delay);
    return { ...IDLE, outcome: 'rejected_batch', sent: batch.length, nextDelayMs: delay };
  }

  /** The retry that brings the waiting items back, when nothing was due now. */
  private scheduleNextDue(queued: OutboxRecord[], at: number): void {
    const waits = queued
      .map((item) => item.next_attempt_at)
      .filter((instant): instant is number => instant !== null);
    if (waits.length === 0 || this.retryTimer !== null) return;
    this.scheduleRetry(Math.max(0, Math.min(...waits) - at));
  }

  private scheduleRetry(delayMs: number): void {
    this.clearRetry();
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.trigger('retry');
    }, delayMs);
  }

  private clearRetry(): void {
    if (this.retryTimer !== null) clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  private setState(patch: Partial<EngineState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener(this.state);
  }
}
